

 import React, { useState } from "react";
import {
  Upload,
  Eye,
  Send,
  Calendar,
  User,
  Hash,
  Camera,
  X,
  Tag,
} from "lucide-react";
import "./commonblog.css";

const BlogUploadUI = () => {
  const [formData, setFormData] = useState({
    mainHeading: "",
    heading: "",
    article: "",
    startDate: "",
    endDate: "",
    scheduleDate: "",
    content: "",
    metaTags: [],
  });

  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [contentImages, setContentImages] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [metaTagInput, setMetaTagInput] = useState("");
  const [showOtherAstrologerInput, setShowOtherAstrologerInput] =
    useState(false);
  const [otherAstrologerName, setOtherAstrologerName] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "article") {
      if (value === "Other") {
        setShowOtherAstrologerInput(true);
        setFormData((prev) => ({ ...prev, [name]: "" }));
      } else {
        setShowOtherAstrologerInput(false);
        setOtherAstrologerName("");
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === "otherAstrologer") {
      setOtherAstrologerName(value);
      setFormData((prev) => ({ ...prev, article: value }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleMetaTagInputChange = (e) => {
    setMetaTagInput(e.target.value);
  };

  const handleMetaTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const newTag = metaTagInput.trim();
      if (newTag && !formData.metaTags.includes(newTag)) {
        setFormData((prev) => ({
          ...prev,
          metaTags: [...prev.metaTags, newTag],
        }));
        setMetaTagInput("");
      }
    }
  };

  const removeMetaTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      metaTags: prev.metaTags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const isImageFile = (file) => {
    const validMimeTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
      "image/svg+xml",
      "image/tiff",
    ];
    const validExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
      ".tiff",
      ".jfif",
    ];

    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    );
    const hasValidMimeType = validMimeTypes.includes(file.type);

    return hasValidMimeType || hasValidExtension;
  };

  const handleMainImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && isImageFile(file)) {
      setMainImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else if (file) {
      alert(
        "Please select a valid image file (JPG, PNG, GIF, BMP, WEBP, SVG, TIFF, JFIF)"
      );
    }
  };

  const handleContentImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => isImageFile(file));

    if (imageFiles.length !== files.length) {
      alert("Some files were not valid images and were skipped.");
    }

    setContentImages((prev) => [...prev, ...imageFiles]);
  };

  const removeContentImage = (index) => {
    setContentImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeMainImage = () => {
    setMainImage(null);
    setMainImagePreview(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.replace(/-/g, "");
  };

  const prepareHtmlContent = (content) => {
    return content;
  };

  
  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const htmlContent = prepareHtmlContent(formData.content);

      const finalAstrologer = showOtherAstrologerInput
        ? otherAstrologerName
        : formData.article;

     
      const queryParams = new URLSearchParams({
        mainHeading: formData.mainHeading,
        startDate: formatDate(formData.startDate),
        endDate: formatDate(formData.endDate),
        scheduleDate: formatDate(formData.scheduleDate),
        heading: formData.heading,
        article: finalAstrologer,
        htmlContent: htmlContent, 
      });

      
      formData.metaTags.forEach((tag) => {
        queryParams.append("metaTags", tag);
      });

     
      const formDataToSend = new FormData();

      if (mainImage) {
        formDataToSend.append("mainImageFile", mainImage);
      }

      contentImages.forEach((image) => {
        formDataToSend.append("contentFiles", image);
      });

      
      const url = `https://heastro.in/heastro-service/astroblogs/save?${queryParams}`;

      const response = await fetch(url, {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      alert("Blog uploaded successfully!");
      
      // Reset form
      setFormData({
        mainHeading: "",
        heading: "",
        article: "",
        startDate: "",
        endDate: "",
        scheduleDate: "",
        content: "",
        metaTags: [],
      });
      setMainImage(null);
      setMainImagePreview(null);
      setContentImages([]);
      setMetaTagInput("");
      setShowOtherAstrologerInput(false);
      setOtherAstrologerName("");
    } catch (error) {
      console.error("Error uploading blog:", error);
      alert(`Error uploading blog: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPreview = () => {
    if (!formData.content) {
      return (
        <p style={{ color: "#9ca3af", fontStyle: "italic" }}>
          No content to preview
        </p>
      );
    }

    const hasHtmlTags = /<[^>]*>/g.test(formData.content);

    if (hasHtmlTags) {
      return (
        <div
          className="preview-html"
          dangerouslySetInnerHTML={{ __html: formData.content }}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "16px",
            backgroundColor: "#fafafa",
            maxHeight: "400px",
            overflow: "auto",
          }}
        />
      );
    } else {
      return (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "16px",
            backgroundColor: "#fafafa",
            whiteSpace: "pre-wrap",
            maxHeight: "400px",
            overflow: "auto",
          }}
        >
          {formData.content}
        </div>
      );
    }
  };

  return (
    <div className="blog-upload-container">
      <div className="blog-upload-wrapper">
        <div className="blog-upload-card">
          <div className="blog-upload-header">
            <h1 className="blog-upload-title">
              <Upload className="header-icon" />
              Blog Upload Panel
            </h1>
            <p className="blog-upload-subtitle">
              Create and publish your blog content
            </p>
          </div>

          <div className="blog-upload-form">
            <div className="form-section">
              <div className="form-grid-2">
                <div>
                  <label className="form-label">
                    <Hash className="form-label-icon" />
                    Main Heading
                  </label>
                  <input
                    type="text"
                    name="mainHeading"
                    value={formData.mainHeading}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter main heading..."
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Hash className="form-label-icon" />
                    SEO Heading
                  </label>
                  <input
                    type="text"
                    name="heading"
                    value={formData.heading}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter SEO heading..."
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-grid-4">
                <div>
                  <label className="form-label">
                    <User className="form-label-icon" />
                    Author
                  </label>
                  <select
                    name="article"
                    value={
                      showOtherAstrologerInput ? "Other" : formData.article
                    }
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select an Author</option>
                    <option value="Astrologer Nisha">Astrologer Nisha</option>
                    <option value="Astrologer Asha">Astrologer Asha</option>
                    <option value="Pandit Deepanshu">Pandit Deepanshu</option>
                    <option value="Jyotishacharya Rashmi">Jyotishacharya Rashmi</option>
                    <option value="Jyotishacharya Priyanshu">Jyotishacharya Priyanshu</option>
                    <option value="Guruji Rahul">Guruji Rahul</option>
                    <option value="Astro Expert Naveen">Astro Expert Naveen</option>
                    <option value="Other">Type New</option>
                  </select>
                </div>

                {showOtherAstrologerInput && (
                  <div>
                    <label className="form-label">
                      <User className="form-label-icon" />
                      New Author Name
                    </label>
                    <input
                      type="text"
                      name="otherAstrologer"
                      value={otherAstrologerName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter new author name..."
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="form-label">
                    <Calendar className="form-label-icon" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Calendar className="form-label-icon" />
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Calendar className="form-label-icon" />
                    Schedule Date
                  </label>
                  <input
                    type="date"
                    name="scheduleDate"
                    value={formData.scheduleDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-grid-2">
                <div>
                  <label className="form-label">
                    <Camera className="form-label-icon" />
                    Main Image
                  </label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      accept="image/*,.jfif"
                      onChange={handleMainImageUpload}
                      className="file-upload-input"
                      id="mainImage"
                    />
                    <label htmlFor="mainImage" className="file-upload-label">
                      {mainImagePreview ? (
                        <div className="file-upload-content selected">
                          <div
                            style={{
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            <img
                              src={mainImagePreview}
                              alt="Preview"
                              style={{
                                width: "120px",
                                height: "120px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #e5e7eb",
                              }}
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                removeMainImage();
                              }}
                              style={{
                                position: "absolute",
                                top: "-52px",
                                right: "-56px",
                                width: "100px",
                                height: "100px",
                                borderRadius: "0%",
                                backgroundColor: "transparent",
                                color: "red",
                                border: "medium", 
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "none",
                              }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                          <p
                            className="file-upload-text"
                            style={{ marginTop: "8px", fontSize: "12px" }}
                          >
                            {mainImage?.name}
                          </p>
                        </div>
                      ) : (
                        <div className="file-upload-content">
                          <Upload className="file-upload-icon" />
                          <p className="file-upload-text">
                            Click to upload main image
                          </p>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#9ca3af",
                              marginTop: "4px",
                            }}
                          >
                            JPG, PNG, GIF, JFIF supported
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    <Camera className="form-label-icon" />
                    Content Images
                  </label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      accept="image/*,.jfif"
                      multiple
                      onChange={handleContentImagesUpload}
                      className="file-upload-input"
                      id="contentImages"
                    />
                    <label
                      htmlFor="contentImages"
                      className="file-upload-label"
                    >
                      <div className="file-upload-content">
                        <Upload className="file-upload-icon" />
                        <p className="file-upload-text">
                          Click to upload content images
                        </p>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            marginTop: "4px",
                          }}
                        >
                          Multiple files supported
                        </p>
                      </div>
                    </label>
                  </div>

                  {contentImages.length > 0 && (
                    <div className="image-list">
                      {contentImages.map((image, index) => (
                        <div key={index} className="image-item">
                          <span className="image-name">{image.name}</span>
                          <div
                            onClick={() => removeContentImage(index)}
                            className="image-remove"
                          >
                            <X className="remove-icon" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <div>
                <label className="form-label">
                  <Tag className="form-label-icon" />
                  Meta Tags
                </label>
                <input
                  type="text"
                  value={metaTagInput}
                  onChange={handleMetaTagInputChange}
                  onKeyDown={handleMetaTagKeyDown}
                  className="form-input"
                  placeholder="Type a meta tag (e.g., <title>...</title>) and press Enter or comma"
                />

                {formData.metaTags.length > 0 && (
                  <div className="meta-tag-chips-container">
                    {formData.metaTags.map((tag, index) => (
                      <div key={index} className="meta-tag-chip">
                        <span>{tag}</span>
                        <button
                          type="button"
                          onClick={() => removeMetaTag(tag)}
                          className="meta-tag-remove-button"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <div>
                <div className="content-editor-header">
                  <label className="form-label">Content</label>
                  <div
                    onClick={() => setPreviewMode(!previewMode)}
                    className="preview-toggle"
                  >
                    <Eye className="preview-icon" />
                    {previewMode ? "Edit" : "Preview"}
                  </div>
                </div>

                {!previewMode ? (
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Write your blog content here... You can use HTML tags for formatting."
                    required
                    style={{ minHeight: "300px" }}
                  />
                ) : (
                  <div className="preview-area">{renderPreview()}</div>
                )}
              </div>
            </div>

            <div className="submit-container">
              <button
                onClick={handleSubmit}
                className={`submit-button ${isLoading ? "disabled" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div>
                    <div className="loading-spinner"></div>
                    Uploading...
                  </div>
                ) : (
                  <div>
                    <Send className="submit-icon" />
                    Publish Blog
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default BlogUploadUI;

